import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';
import { getCartItemTotal } from '../utils/pricing';

function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shippingCost, setShippingCost] = useState(location.state?.shippingCost || 0);
  const [shippingMethod, setShippingMethod] = useState(location.state?.shippingMethod || '');
  const [estimatedDelivery, setEstimatedDelivery] = useState(location.state?.estimatedDelivery || null);
  const [paymentMethod, setPaymentMethod] = useState(''); // '' | 'online' | 'cod'
  const [formData, setFormData] = useState({
    addressLine1: '',
    addressLine2: '',
    landmark: '',
    building: '',
  });

  useEffect(() => {
    // Load cart from database
    if (token) {
      fetchCart();
    }

    // Load user addresses
    if (user?.id) {
      fetchAddresses();
    }
  }, [token, user?.id]);

  const fetchCart = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/cart`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setCartItems(data.data || []);
    } catch (err) {
      console.error('Error loading cart:', err);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses/${user.id}`);
      const data = await response.json();
      setAddresses(data.data || []);
      
      // Auto-select default address
      const defaultAddress = data.data?.find(addr => addr.isDefault);
      if (defaultAddress) {
        setSelectedAddressId(defaultAddress.id);
      }
    } catch (err) {
      console.log('Error fetching addresses:', err);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          ...formData,
          isDefault: addresses.length === 0, // First address is default
        }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('✅ Address added successfully!');
        setFormData({ addressLine1: '', addressLine2: '', landmark: '', building: '' });
        setShowNewAddressForm(false);
        fetchAddresses();
      } else {
        alert('❌ Error adding address: ' + data.message);
      }
    } catch (err) {
      alert('Connection error. Make sure backend is running.');
      console.log(err);
    }
    setLoading(false);
  };

  const subtotalINR = Math.max(0, cartItems.reduce((sum, item) => {
    return sum + getCartItemTotal(item).total;
  }, 0));

  // Calculate GST based on product GST percentages
  const gstAmount = Math.round(cartItems.reduce((sum, item) => {
    const itemTotal = getCartItemTotal(item).total;
    const itemGst = item.gst || 5;
    return sum + (itemTotal * (itemGst / 100));
  }, 0));

  const totalINR = Math.round(subtotalINR + gstAmount + (Number(shippingCost) || 0));

  const handlePlaceCODOrder = async () => {
    if (!selectedAddressId) {
      alert('⚠️ Please select or add a delivery address!');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/create-upi-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          userId: user.id,
          amount: totalINR,
          cartItems: cartItems,
          addressId: selectedAddressId,
          shippingMethod: shippingMethod,
          shippingCost: Number(shippingCost) || 0,
          estimatedDelivery: estimatedDelivery,
          userEmail: user.email,
          userPhone: user.phone,
          paymentMethod: 'cod',
        }),
      });

      const data = await response.json();
      if (response.ok) {
        // Navigate to order confirmation with COD message
        navigate('/order-confirmation/' + data.data.orderId, {
          state: { paymentMethod: 'cod' },
        });
      } else {
        alert('Error creating order: ' + data.message);
      }
    } catch (err) {
      alert('Connection error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayU = () => {
    if (!selectedAddressId) {
      alert('⚠️ Please select or add a delivery address!');
      return;
    }

    if (!paymentMethod) {
      alert('⚠️ Please select a payment method!');
      return;
    }

    if (paymentMethod === 'cod') {
      handlePlaceCODOrder();
      return;
    }

    // Online payment - navigate to PayU payment page
    navigate('/payment', {
      state: {
        totalINR,
        cartItems,
        selectedAddressId,
        shippingMethod,
        shippingCost: Number(shippingCost) || 0,
        estimatedDelivery,
      },
    });
  };

  if (cartItems.length === 0) {
    return (
      <motion.div
        style={{
          minHeight: '100vh',
          backgroundColor: colors.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: spacing.xl,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <motion.div
          style={{
            textAlign: 'center',
            backgroundColor: 'white',
            borderRadius: '1rem',
            padding: spacing.xl,
            boxShadow: shadows.md,
          }}
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
        >
          <p style={{ fontSize: '1.5rem', color: colors.text.primary, marginBottom: spacing.lg, fontWeight: 700 }}>
            🛒 Your cart is empty
          </p>
          <motion.button
            onClick={() => navigate('/products')}
            style={{
              backgroundColor: colors.primary[600],
              color: 'white',
              padding: `${spacing.md} ${spacing.xl}`,
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: '1rem',
            }}
            whileHover={{ backgroundColor: colors.primary[700] }}
            whileTap={{ scale: 0.95 }}
          >
            Continue Shopping
          </motion.button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: colors.background }}>
      {/* Header */}
      <motion.div
        style={{
          background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
          color: 'white',
          padding: `${spacing.xl} ${spacing.lg}`,
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <div style={{ maxWidth: '90rem', margin: '0 auto' }}>
          <motion.h1
            style={{
              fontSize: '2rem',
              fontWeight: 900,
              margin: 0,
            }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            🛍️ Checkout
          </motion.h1>
        </div>
      </motion.div>

      {/* Main Content */}
      <div style={{ maxWidth: '90rem', margin: '0 auto', padding: spacing.xl, display: 'grid', gridTemplateColumns: { base: '1fr', md: '2fr 1fr' }, gap: spacing.xl }}>
        {/* Left Side */}
        <motion.div
          style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Delivery Address */}
          <motion.div
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              padding: spacing.lg,
              boxShadow: shadows.md,
            }}
          >
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: 900,
                color: colors.text.primary,
                marginBottom: spacing.lg,
                margin: 0,
              }}
            >
              📍 Delivery Address
            </h2>

            {addresses.length > 0 && !showNewAddressForm && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, marginBottom: spacing.lg }}>
                {addresses.map((address) => (
                  <motion.label
                    key={address.id}
                    style={{
                      display: 'block',
                      padding: spacing.md,
                      border: `2px solid ${selectedAddressId === address.id ? colors.primary[600] : colors.border}`,
                      borderRadius: '0.5rem',
                      cursor: 'pointer',
                      backgroundColor: selectedAddressId === address.id ? colors.primary[25] : 'white',
                      transition: 'all 0.2s ease',
                    }}
                    whileHover={{ borderColor: colors.primary[400] }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: spacing.md }}>
                      <input
                        type="radio"
                        name="address"
                        value={address.id}
                        checked={selectedAddressId === address.id}
                        onChange={() => setSelectedAddressId(address.id)}
                        style={{ marginTop: '0.25rem', cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 700, color: colors.text.primary, margin: 0, marginBottom: spacing.xs }}>
                          {address.building}
                        </p>
                        <p style={{ fontSize: '0.95rem', color: colors.text.secondary, margin: 0 }}>
                          {address.addressLine1}
                        </p>
                        {address.addressLine2 && (
                          <p style={{ fontSize: '0.95rem', color: colors.text.secondary, margin: 0 }}>
                            {address.addressLine2}
                          </p>
                        )}
                        {address.landmark && (
                          <p style={{ fontSize: '0.95rem', color: colors.text.secondary, margin: 0 }}>
                            Near: {address.landmark}
                          </p>
                        )}
                        {address.isDefault && (
                          <span
                            style={{
                              display: 'inline-block',
                              marginTop: spacing.sm,
                              padding: `${spacing.xs} ${spacing.sm}`,
                              backgroundColor: colors.primary[600],
                              color: 'white',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              borderRadius: '9999px',
                            }}
                          >
                            Default
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.label>
                ))}
              </div>
            )}

            {showNewAddressForm && (
              <motion.form
                onSubmit={handleAddAddress}
                style={{
                  backgroundColor: colors.primary[25],
                  padding: spacing.lg,
                  borderRadius: '0.5rem',
                  marginBottom: spacing.lg,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.md,
                }}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
              >
                <input
                  type="text"
                  placeholder="Building / Apartment Name"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  required
                  style={{
                    padding: spacing.md,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary[600];
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <input
                  type="text"
                  placeholder="Address Line 1 (House no, street)"
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  required
                  style={{
                    padding: spacing.md,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary[600];
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <input
                  type="text"
                  placeholder="Address Line 2 (Area, Colony) - Optional"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  style={{
                    padding: spacing.md,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary[600];
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <input
                  type="text"
                  placeholder="Landmark - Optional"
                  value={formData.landmark}
                  onChange={(e) => setFormData({ ...formData, landmark: e.target.value })}
                  style={{
                    padding: spacing.md,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '0.5rem',
                    fontSize: '0.95rem',
                    outline: 'none',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.primary[600];
                    e.target.style.boxShadow = `0 0 0 3px ${colors.primary[100]}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.border;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{ display: 'flex', gap: spacing.md, marginTop: spacing.md }}>
                  <motion.button
                    type="submit"
                    disabled={loading}
                    style={{
                      flex: 1,
                      backgroundColor: colors.primary[600],
                      color: 'white',
                      padding: spacing.md,
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                      opacity: loading ? 0.6 : 1,
                    }}
                    whileHover={{ backgroundColor: colors.primary[700] }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {loading ? '⏳ Saving...' : '✅ Save Address'}
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={() => setShowNewAddressForm(false)}
                    style={{
                      flex: 1,
                      backgroundColor: colors.neutral[200],
                      color: colors.text.primary,
                      padding: spacing.md,
                      border: 'none',
                      borderRadius: '0.5rem',
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                    whileHover={{ backgroundColor: colors.neutral[300] }}
                    whileTap={{ scale: 0.95 }}
                  >
                    ❌ Cancel
                  </motion.button>
                </div>
              </motion.form>
            )}

            {!showNewAddressForm && (
              <motion.button
                onClick={() => setShowNewAddressForm(true)}
                style={{
                  width: '100%',
                  padding: spacing.md,
                  border: `2px solid ${colors.primary[600]}`,
                  backgroundColor: 'white',
                  color: colors.primary[600],
                  borderRadius: '0.5rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                whileHover={{ backgroundColor: colors.primary[25] }}
                whileTap={{ scale: 0.95 }}
              >
                ➕ Add New Address
              </motion.button>
            )}
          </motion.div>

          {/* Payment Method */}
          <motion.div
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              padding: spacing.lg,
              boxShadow: shadows.md,
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: 900,
                color: colors.text.primary,
                marginBottom: spacing.lg,
                margin: 0,
              }}
            >
              💳 Payment Method
            </h2>
            <p style={{ fontSize: '0.9rem', color: colors.text.secondary, marginBottom: spacing.lg }}>
              Select how you would like to pay
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {/* Online Payment */}
              <motion.label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                  padding: spacing.lg,
                  border: `2px solid ${paymentMethod === 'online' ? colors.primary[600] : colors.border}`,
                  borderRadius: '0.75rem',
                  cursor: 'pointer',
                  backgroundColor: paymentMethod === 'online' ? colors.primary[25] : 'white',
                  transition: 'all 0.2s ease',
                }}
                whileHover={{ borderColor: colors.primary[400] }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="online"
                  checked={paymentMethod === 'online'}
                  onChange={() => setPaymentMethod('online')}
                  style={{ cursor: 'pointer', width: '1.2rem', height: '1.2rem' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
                    <span style={{ fontSize: '1.5rem' }}>💻</span>
                    <span style={{ fontWeight: 800, color: colors.text.primary, fontSize: '1.05rem' }}>Pay Online</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: colors.text.secondary }}>
                    Pay via PayU (Credit/Debit Card, Net Banking, UPI, Wallet) — Secure & Instant
                  </p>
                </div>
              </motion.label>

              {/* COD */}
              <motion.div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.md,
                  padding: spacing.lg,
                  border: `2px solid ${colors.border}`,
                  borderRadius: '0.75rem',
                  cursor: 'not-allowed',
                  backgroundColor: '#f5f5f5',
                  opacity: 0.6,
                  transition: 'all 0.2s ease',
                }}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cod"
                  disabled
                  style={{ cursor: 'not-allowed', width: '1.2rem', height: '1.2rem' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
                    <span style={{ fontSize: '1.5rem' }}>💵</span>
                    <span style={{ fontWeight: 800, color: colors.text.secondary, fontSize: '1.05rem' }}>Cash on Delivery</span>
                  </div>
                  <p style={{ margin: 0, fontSize: '0.85rem', color: '#ef4444', fontWeight: 600 }}>
                    ❌ COD is unavailable
                  </p>
                  <p style={{ margin: `${spacing.xs} 0 0`, fontSize: '0.8rem', color: colors.text.secondary }}>
                    Please use PayU (Credit/Debit Card, Net Banking, UPI, Wallet)
                  </p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Order Items */}
          <motion.div
            style={{
              backgroundColor: 'white',
              borderRadius: '1rem',
              padding: spacing.lg,
              boxShadow: shadows.md,
            }}
          >
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: 900,
                color: colors.text.primary,
                marginBottom: spacing.lg,
                margin: 0,
              }}
            >
              📦 Order Items
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, borderTop: `1px solid ${colors.border}`, paddingTop: spacing.lg }}>
                  {cartItems.map((item) => {
                    const { total: itemTotal, offerApplied } = getCartItemTotal(item);
                    return (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontWeight: 700, color: colors.text.primary, margin: 0, marginBottom: spacing.xs }}>
                          {item.name}
                        </p>
                        <p style={{ fontSize: '0.95rem', color: colors.text.secondary, margin: 0 }}>
                          {!item.hasNoSizeSelection && item.size_name && `Size: ${item.size_name} `}
                          {item.side ? `— ${item.side === 'right' ? 'Right' : 'Left'} side ` : ''}
                          | Qty: {item.quantity}
                        </p>
                        {offerApplied && (
                          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#16a34a', backgroundColor: '#dcfce7', padding: '0.125rem 0.5rem', borderRadius: '9999px' }}>
                            {offerApplied.label || `Extra ${offerApplied.effectiveDiscount || offerApplied.discountPercent}% off`}
                          </span>
                        )}
                  </div>
                  <p style={{ fontWeight: 700, color: colors.primary[600], fontSize: '1.05rem' }}>
                    ₹{itemTotal}
                  </p>
                </div>
                  );
              })}
            </div>
          </motion.div>
        </motion.div>

        {/* Right Side - Order Summary */}
        <motion.div
          style={{
            backgroundColor: 'white',
            borderRadius: '1rem',
            padding: spacing.lg,
            boxShadow: shadows.md,
            height: 'fit-content',
            position: 'sticky',
            top: '6rem',
          }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2
            style={{
              fontSize: '1.5rem',
              fontWeight: 900,
              color: colors.text.primary,
              marginBottom: spacing.lg,
              margin: 0,
            }}
          >
            Order Summary
          </h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, marginBottom: spacing.lg, paddingBottom: spacing.lg, borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text.secondary }}>
              <span>Subtotal</span>
              <span style={{ fontWeight: 600 }}>₹{subtotalINR}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text.secondary }}>
              <span>GST</span>
              <span style={{ fontWeight: 600 }}>₹{gstAmount}</span>
            </div>
            {shippingMethod && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: colors.text.secondary }}>
                <span>Shipping ({shippingMethod})</span>
                <span style={{ fontWeight: 600, color: colors.primary[600] }}>₹{(Number(shippingCost) || 0).toFixed(2)}</span>
              </div>
            )}
            {estimatedDelivery && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem', color: colors.text.secondary }}>
                <span>📅 Est. Delivery</span>
                <span>{new Date(estimatedDelivery).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })}</span>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, marginTop: spacing.lg, paddingTop: spacing.lg, borderTop: `1px solid ${colors.border}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: '1.1rem', fontWeight: 700, color: colors.text.primary }}>Total</span>
              <span style={{ fontSize: '1.75rem', fontWeight: 900, color: colors.primary[600] }}>₹{totalINR}</span>
            </div>
            {paymentMethod === 'cod' && (
              <div style={{ padding: `${spacing.sm} ${spacing.md}`, backgroundColor: '#f0fdf4', borderRadius: '0.5rem', border: '1px solid #22c55e' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#16a34a' }}>💵 Pay ₹{totalINR} at delivery — No advance payment</p>
              </div>
            )}
            {paymentMethod === 'online' && (
              <div style={{ padding: `${spacing.sm} ${spacing.md}`, backgroundColor: colors.primary[25], borderRadius: '0.5rem', border: `1px solid ${colors.primary[200]}` }}>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: colors.primary[600] }}>💻 Pay ₹{totalINR} via PayU (Cards/Net Banking/UPI/Wallet)</p>
              </div>
            )}
          </div>

          <motion.button
            onClick={handleProceedToPayU}
            disabled={loading || !selectedAddressId || !paymentMethod || totalINR === 0}
            style={{
              width: '100%',
              padding: spacing.lg,
              backgroundColor: loading || !selectedAddressId || !paymentMethod || totalINR === 0 ? colors.neutral[300] : colors.primary[600],
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: 700,
              fontSize: '1rem',
              cursor: loading || !selectedAddressId || !paymentMethod || totalINR === 0 ? 'not-allowed' : 'pointer',
              minHeight: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: loading || !selectedAddressId || !paymentMethod || totalINR === 0 ? 0.6 : 1,
              marginTop: spacing.lg,
            }}
            whileHover={!(loading || !selectedAddressId || !paymentMethod || totalINR === 0) ? { backgroundColor: colors.primary[700] } : {}}
            whileTap={!(loading || !selectedAddressId || !paymentMethod || totalINR === 0) ? { scale: 0.95 } : {}}
          >
            {loading ? (
              '⏳ Creating Order...'
            ) : paymentMethod === 'cod' ? (
              '✅ Confirm Order (Cash on Delivery)'
            ) : paymentMethod === 'online' ? (
              '💳 Proceed to PayU Payment'
            ) : (
              'Select Payment Method'
            )}
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}

export default Checkout;